import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import {
  PushNotifications,
  PushNotificationSchema,
  Token,
} from '@capacitor/push-notifications'
import axios from 'axios'

const STORAGE_KEY = 'push_token_data'

interface StoredPushToken {
  token: string
  provider: string
  device_id?: string
  device_name?: string
}

export const registerStoredPushToken = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false
  }

  try {
    const storedData = localStorage.getItem(STORAGE_KEY)
    if (!storedData) {
      console.log('No stored push token to register')
      return false
    }

    const tokenData: StoredPushToken = JSON.parse(storedData)

    // Send token to backend
    await axios.post('/api/push-notifications/register', tokenData)

    console.log('Push token registered with backend')
    return true
  } catch (error) {
    console.error('Failed to register push token with backend:', error)
    return false
  }
}

/**
 * Clears the notification badge on the app icon
 */
export const clearBadge = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  try {
    // Remove all delivered notifications from notification center
    await PushNotifications.removeAllDeliveredNotifications()
    console.log('Cleared notification badge and delivered notifications')
  } catch (error) {
    console.error('Failed to clear notification badge:', error)
  }
}

export const initializeNotifications = async () => {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  try {
    // Set up listeners
    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('Push registration success, token: ' + token.value)

      // Determine provider based on platform
      const platform = Capacitor.getPlatform()
      const provider = platform === 'ios' ? 'apns' : 'fcm'

      // Store token locally to be sent after authentication
      const tokenData: StoredPushToken = {
        token: token.value,
        provider: provider,
        device_id: undefined,
        device_name: undefined,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokenData))
      console.log('Push token stored locally, will register after login')

      // Try to register immediately (will succeed if user is already logged in)
      await registerStoredPushToken()
    })

    PushNotifications.addListener('registrationError', (error: unknown) => {
      console.error('Error on registration: ' + JSON.stringify(error))
    })

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('Push notification received: ', notification)
      }
    )

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: unknown) => {
        console.log('Push notification action performed', notification)
      }
    )

    // Listen for app state changes to clear badge when app becomes active
    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        console.log('App became active, clearing notification badge')
        await clearBadge()
      }
    })

    // Clear badge when app first opens
    await clearBadge()

    // Request permissions for push notifications
    const result = await PushNotifications.requestPermissions()

    if (result.receive === 'granted') {
      // Register with Apple / Google to receive push via APNS/FCM
      await PushNotifications.register()
    }
  } catch (error) {
    console.error('Error initializing notifications:', error)
  }
}

export const isMobile = () => Capacitor.isNativePlatform()
export const getPlatform = () => Capacitor.getPlatform()
